/**
 * feature/analysis — spec §F6, the analysis toolbox.
 *
 * "Right dock (390px, draggable header, ✕, '← All tools' back), home = intro line (+
 * AOI-active note) + 8 tool cards (icon tile colour per Part H) + 'Clear result layers
 * (n)'. Tool run: params form → POST → progress bar polling job → result panels (tables
 * `.result-box`, charts, buttons) → optional result layers on map (registered in legend
 * block (e)). Errors → toast + inline message."
 *
 * Markup and class names are the mockup's `#analysisDock` so the dock is pixel-identical;
 * what is not the mockup's:
 *
 * * **it is mounted only while it is open.** The mockup toggled a `.show` class on a node
 *   that lived for the life of the page, which kept a finished result on screen for the
 *   whole session. Here, closing the dock unmounts the running tool — which stops its poll,
 *   the one thing that must not outlive the panel (a timer against a throttled endpoint).
 *   A run whose panel is closed keeps going server-side and its result stays in the §E7
 *   cache, so re-running the same parameters answers instantly; §F5.6's block (e) is where
 *   a *result* persists, which is the part a reader actually needs to keep.
 * * **which tool is open is local state.** It is not shared, not permalinked and not §F1
 *   state — the only thing the rest of the app needs to know is whether the dock is open
 *   (`analysisOpen`), because §F5.10's button and the §F3 nav item both toggle that.
 * * **the header drags through `lib/draggable.ts`**, like every other floating widget
 *   (§F5.11), rather than being fixed to the mockup's `top:12px;right:56px`. The open flag
 *   is passed to the hook as `enabled`: this component is mounted while it is closed and
 *   renders `null`, so without it the drag effect would run once at page load against a
 *   `null` element and never again — a header that looks grabbable and is not (NOTES #637).
 * * **each tool panel renders inside its own `ErrorBoundary`.** A panel dereferences the
 *   result while it renders — `result.rows.length`, `result.matrix.classes` — and a shape it
 *   did not expect is a TypeError that would otherwise unmount the React root and leave a
 *   blank portal (NOTES #650). Keyed on the tool id, so switching tools clears a boundary
 *   that has already caught: the reader gets the next tool's form, not the last one's error.
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDraggable } from '../../lib/draggable';
import { Icon } from '../../lib/icons';
import { useAppStore } from '../../lib/state';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import type { ToolId } from '../../lib/analysis';
import { ToolHome } from './ToolHome';
import { Compare } from './tools/Compare';
import { Aoi } from './tools/Aoi';
import { Zonal } from './tools/Zonal';
import { Suitability } from './tools/Suitability';
import { Change } from './tools/Change';
import { Spi } from './tools/Spi';
import { Buffer } from './tools/Buffer';
import { Overlay } from './tools/Overlay';

/** What every tool component is handed: how to get back to the eight cards. */
export interface ToolProps {
  onBack: () => void;
}

const TOOL_VIEWS: Record<ToolId, (props: ToolProps) => JSX.Element> = {
  compare: Compare,
  aoi: Aoi,
  zonal: Zonal,
  suitability: Suitability,
  change: Change,
  spi: Spi,
  buffer: Buffer,
  overlay: Overlay,
};

export function AnalysisDock() {
  const { t } = useTranslation();
  const open = useAppStore((s) => s.analysisOpen);
  const setOpen = useAppStore((s) => s.setAnalysisOpen);
  const [tool, setTool] = useState<ToolId | null>(null);

  const dockRef = useRef<HTMLDivElement | null>(null);
  const headRef = useRef<HTMLElement | null>(null);
  useDraggable(dockRef, headRef, { enabled: open });

  if (!open) return null;

  const View = tool ? TOOL_VIEWS[tool] : null;

  return (
    <div id="analysisDock" ref={dockRef}>
      <header ref={headRef}>
        <Icon name="wrench" size={16} />
        <h3>{t('analysis.title')}</h3>
        <button
          type="button"
          id="adClose"
          onClick={() => setOpen(false)}
          aria-label={t('widget.close')}
          title={t('widget.close')}
        >
          ×
        </button>
      </header>
      <div id="adBody">
        {View ? (
          <ErrorBoundary variant="panel" key={tool}>
            <View onBack={() => setTool(null)} />
          </ErrorBoundary>
        ) : (
          <ToolHome onOpen={setTool} />
        )}
      </div>
    </div>
  );
}

export default AnalysisDock;
